/* ==========================================================================
   BODYFONT — Product Page V2 (bfv2)
   One selection state (concern + pack + mix) drives every display on the page.
   Cart adds go through the store's existing header cart drawer — no second
   cart system, no duplicated pixels.
   Scoped entirely to [data-bfv2-root]; cannot touch the original page.
   ========================================================================== */
(function () {
  'use strict';

  /* The stylesheet and this script are referenced by every bfv2 section so the
     page still works if one is removed. Identical <script src> tags execute
     once per tag, so bail out on the second. */
  if (window.__BFV2_LOADED__) return;
  window.__BFV2_LOADED__ = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function scrollToEl(el) {
    if (!el) return;
    el.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
  }

  /* ---------------------------------------------------------------- store */

  function Controller(root) {
    this.root = root;
    this.ac = new AbortController();
    var node = $('[data-bfv2-data]', root);
    if (!node) return;
    try { this.data = JSON.parse(node.textContent); } catch (e) { return; }

    this.busy = false;
    this.ready = false;
    this.customised = false;      /* has the shopper picked a mix themselves? */

    this.pairDark = 1;
    this.setDefaults();
    this.restoreFromUrl();
    this.bindControls();
    this.bindCart();
    this.bindSticky();
    this.ready = true;
    this.render();
  }

  Controller.prototype.on = function (el, evt, fn, opts) {
    if (!el) return;
    var o = opts || {};
    o.signal = this.ac.signal;
    el.addEventListener(evt, fn, o);
  };

  Controller.prototype.setDefaults = function () {
    var d = this.data;
    this.tier = Math.min(Math.max(d.defaultTier || 0, 0), d.tiers.length - 1);
    this.concern = d.defaultConcern || 0;
    this.dark = d.pairOffer && d.tiers[this.tier].packs === 2 ? this.pairDark : this.nearestAllowed(this.tier, this.pureDark(this.concern, d.tiers[this.tier].packs));
  };

  /* all packs of the chosen concern */
  Controller.prototype.pureDark = function (concern, packs) {
    return concern === 0 ? 0 : packs;
  };

  Controller.prototype.currentTier = function () { return this.data.tiers[this.tier]; };

  /* The Patch option value a composition needs: pure at either end, mixed between. */
  Controller.prototype.patchValue = function (dark, packs) {
    var d = this.data;
    if (dark <= 0) return d.concernValues[0];
    if (dark >= packs) return d.concernValues[1];
    return d.mixPattern.replace('%b%', packs - dark).replace('%d%', dark);
  };

  Controller.prototype.variantFor = function (tierIdx, dark) {
    var t = this.data.tiers[tierIdx];
    if (!t) return null;
    if (this.data.pairOffer) {
      var d = this.data, green = d.pairSingles[0], orange = d.pairSingles[1];
      if (!green || !orange) return null;
      var countG = t.packs - dark, countO = dark;
      var items = [];
      if (countG) items.push({id: green.id, quantity: countG});
      if (countO) items.push({id: orange.id, quantity: countO});
      var was = green.total * countG + orange.total * countO;
      var saving = t.packs === 2 ? d.pairDiscount : 0;
      var available = (!countG || (countG === 2 ? green.pairAvailable : green.available)) &&
                      (!countO || (countO === 2 ? orange.pairAvailable : orange.available));
      return {id: t.packs === 1 ? items[0].id : null, items: items, available: available,
        total: was - saving, money: this.money(was - saving), wasCents: was,
        was: saving ? this.money(was) : null, save: saving ? this.money(saving) : null};
    }
    return this.data.variants[t.packValue + '|' + this.patchValue(dark, t.packs)] || null;
  };

  Controller.prototype.money = function (cents) {
    return new Intl.NumberFormat('en-US', {style:'currency', currency:this.data.currency || 'USD'}).format(cents / 100);
  };

  /* Pricing falls back to the tier's proposed figures when no variant exists yet,
     so the layout can be reviewed — but nothing can be bought in that state. */
  Controller.prototype.pricingFor = function (tierIdx, dark) {
    var v = this.variantFor(tierIdx, dark);
    if (v) return { p: v, variant: v, buyable: !!v.available };
    return { p: this.data.tiers[tierIdx].proposed, variant: null, buyable: false };
  };

  /* Which compositions a tier offers, in the order it lists them. A tier with
     no list falls back to every combination. */
  Controller.prototype.allowedDarks = function (tierIdx) {
    var t = this.data.tiers[tierIdx];
    if (t.mixDarks && t.mixDarks.length) {
      return t.mixDarks.filter(function (k) { return k >= 0 && k <= t.packs; });
    }
    var all = [];
    for (var k = 0; k <= t.packs; k++) all.push(k);
    return all;
  };

  /* Snap a composition onto one this tier actually offers. */
  Controller.prototype.nearestAllowed = function (tierIdx, dark) {
    var list = this.allowedDarks(tierIdx);
    if (!list.length) return 0;
    if (list.indexOf(dark) > -1) return dark;
    return list.reduce(function (best, k) {
      return Math.abs(k - dark) < Math.abs(best - dark) ? k : best;
    }, list[0]);
  };

  Controller.prototype.compositions = function (tierIdx) {
    var self = this, t = this.data.tiers[tierIdx];
    return this.allowedDarks(tierIdx).map(function (k) {
      var v = self.variantFor(tierIdx, k);
      return { dark: k, blemish: t.packs - k, variant: v, available: !!(v && v.available) };
    });
  };

  Controller.prototype.boxesFor = function (dark, packs) {
    var out = [], k;
    for (k = 0; k < packs - dark; k++) out.push(0);
    for (k = 0; k < dark; k++) out.push(1);
    return out;
  };

  Controller.prototype.compositionLabel = function (dark, packs) {
    var d = this.data;
    if (dark <= 0) return packs + ' × ' + d.concernShort[0];
    if (dark >= packs) return packs + ' × ' + d.concernShort[1];
    if (d.freeThirdPack && d.defaultConcern === 1) return dark + ' × ' + d.concernShort[1] + ' + ' + (packs - dark) + ' × ' + d.concernShort[0];
    return (packs - dark) + ' × ' + d.concernShort[0] + ' + ' + dark + ' × ' + d.concernShort[1];
  };

  /* Honour ?variant= only when it maps to one of the current tiers. An old
     two-pack or the add-on cannot be relabelled as a new bundle, so it visibly
     falls back to the default tier. */
  Controller.prototype.restoreFromUrl = function () {
    var want, params;
    try { params = new URLSearchParams(location.search); want = params.get('variant'); } catch (e) { return; }
    if (this.data.pairOffer) {
      var pairTier = this.data.tiers.findIndex(function (t) { return t.packs === 2; });
      if (params.get('pack') === '2' && pairTier >= 0) {
        this.tier = pairTier;
        var mix = Number(params.get('mix'));
        this.dark = params.has('mix') && [0,1,2].indexOf(mix) >= 0 ? mix : 1;
        this.pairDark = this.dark;
      } else if (want) {
        var single = this.data.pairSingles[this.data.defaultConcern || 0];
        if (single && String(single.id) === want) this.tier = 0;
        else this.unmappedVariant = want;
        this.dark = this.data.defaultConcern || 0;
      }
      return;
    }
    if (!want) return;
    var d = this.data;
    for (var ti = 0; ti < d.tiers.length; ti++) {
      var t = d.tiers[ti];
      var allowed = this.allowedDarks(ti);
      for (var ai = 0; ai < allowed.length; ai++) {
        var k = allowed[ai];
        var v = this.variantFor(ti, k);
        if (v && String(v.id) === String(want)) {
          this.tier = ti;
          this.dark = k;
          this.concern = d.freeThirdPack ? (d.defaultConcern || 0) : (k >= t.packs ? 1 : 0);
          this.customised = k > 0 && k < t.packs;
          return;
        }
      }
    }
    this.unmappedVariant = want;   /* reported to the shopper, not silently reused */
  };

  Controller.prototype.syncUrl = function (variant) {
    if (!variant || !window.history || !history.replaceState) return;
    try {
      var u = new URL(location.href);
      if (this.data.pairOffer) {
        u.searchParams.set('variant', this.data.pairSingles[this.data.defaultConcern || 0].id);
        if (this.currentTier().packs === 2) {
          u.searchParams.set('pack', '2'); u.searchParams.set('mix', this.dark);
        } else { u.searchParams.delete('pack'); u.searchParams.delete('mix'); }
      } else u.searchParams.set('variant', variant.id);
      history.replaceState({}, '', u.toString());
    } catch (e) { /* non-fatal */ }
  };

  /* ------------------------------------------------------------- controls */

  Controller.prototype.bindControls = function () {
    var self = this;

    $$('[data-bfv2-concern]', this.root).forEach(function (btn) {
      self.on(btn, 'click', function () {
        var c = parseInt(btn.getAttribute('data-bfv2-concern'), 10);
        if (c === self.concern) return;
        self.concern = c;
        /* until the shopper customises, every pack follows the concern */
        if (!self.customised) self.dark = self.nearestAllowed(self.tier, self.pureDark(c, self.currentTier().packs));
        self.render();
      });
    });

    /* tier rows and mix rows are rendered by JS, so both are delegated */
    this.on(this.root, 'click', function (e) {
      var productChoice = e.target.closest ? e.target.closest('[data-bfv2-product-choice]') : null;
      if (productChoice && self.root.contains(productChoice)) {
        if (productChoice.getAttribute('aria-disabled') === 'true' || productChoice.getAttribute('aria-current') === 'page') e.preventDefault();
        return;
      }
      var tierBtn = e.target.closest ? e.target.closest('[data-bfv2-tier]') : null;
      if (tierBtn && self.root.contains(tierBtn)) {
        var next = parseInt(tierBtn.getAttribute('data-bfv2-tier'), 10);
        if (next === self.tier) return;
        var oldPacks = self.currentTier().packs;
        var newPacks = self.data.tiers[next].packs;
        self.tier = next;
        if (self.data.pairOffer) {
          self.dark = newPacks === 2 ? self.pairDark : (self.data.defaultConcern || 0);
          self.concern = self.data.defaultConcern || 0;
        } else if (self.customised && oldPacks > 0) {
          /* carry the shopper's ratio across, never the old quantities */
          var scaled = Math.round(self.dark / oldPacks * newPacks);
          self.dark = self.nearestAllowed(next, Math.min(Math.max(scaled, 0), newPacks));
        } else {
          self.dark = self.nearestAllowed(next, self.pureDark(self.concern, newPacks));
        }
        if (newPacks === 1) self.customised = false;
        self.closeMix();
        self.render();
        return;
      }
      var mixBtn = e.target.closest ? e.target.closest('[data-bfv2-mixopt]') : null;
      if (mixBtn && self.root.contains(mixBtn)) {
        if (mixBtn.getAttribute('data-available') === 'false') return;
        var k = parseInt(mixBtn.getAttribute('data-bfv2-mixopt'), 10);
        if (isNaN(k)) return;
        if (self.data.pairOffer) self.pairDark = k;
        self.dark = k;
        var packs = self.currentTier().packs;
        self.customised = k > 0 && k < packs;
        if (self.data.pairOffer) {
          // A pure pair of the other patch opens its corresponding product
          // page, with both pack count and composition preserved.
          var target = k === 0 ? 0 : (k === packs ? 1 : self.data.defaultConcern);
          if (target !== self.data.defaultConcern) {
            var single = self.data.pairSingles[target];
            location.href = single.url + '?variant=' + single.id + '&pack=2&mix=' + k + '#custom_buy_box';
            return;
          }
          self.concern = self.data.defaultConcern;
        } else {
          if (k <= 0) self.concern = 0;
          if (k >= packs) self.concern = 1;
        }
        self.render();
      }
    });

    this.on(this.root, 'keydown', function (e) {
      if (!(self.data.freeThirdPack || self.data.pairOffer) || !/^(ArrowLeft|ArrowRight|ArrowUp|ArrowDown)$/.test(e.key)) return;
      var current = e.target.closest ? e.target.closest('[data-bfv2-mixopt]') : null;
      if (!current) return;
      var choices = $$('[data-bfv2-mixopt]:not(:disabled)', self.root);
      var index = choices.indexOf(current);
      if (index < 0 || !choices.length) return;
      e.preventDefault();
      var delta = /Right|Down/.test(e.key) ? 1 : -1;
      var next = choices[(index + delta + choices.length) % choices.length];
      var dark = next.getAttribute('data-bfv2-mixopt');
      next.click();
      var fresh = $('[data-bfv2-mixopt="' + dark + '"]', self.root);
      if (fresh) fresh.focus({preventScroll:true});
    });

    var mixToggle = $('[data-bfv2-mix-toggle]', this.root);
    var mixPanel = $('[data-bfv2-mix-panel]', this.root);
    if (mixToggle && mixPanel) {
      this.on(mixToggle, 'click', function () {
        var open = mixToggle.getAttribute('aria-expanded') === 'true';
        mixToggle.setAttribute('aria-expanded', String(!open));
        mixPanel.hidden = open;
        var cur = $('[data-bfv2-mix-current]', self.root);
        if (cur) cur.hidden = !open ? true : cur.hidden;
        self.renderMixSummary();
      });
    }

    /* gallery */
    var track = $('[data-bfv2-gal-track]', this.root);
    if (track) {
      // Once the first photo arrives, prepare the next two slides for swiping.
      var firstPhoto = $('img[fetchpriority="high"]', track) || $('img', track);
      var startBuffer = function () {
        var afterDecode = function () {
          requestAnimationFrame(function () { requestAnimationFrame(function () {
            if (self.ac.signal.aborted) return;
            self.galleryBuffered = true;
            self.warmGallery();
          }); });
        };
        if (firstPhoto && firstPhoto.decode) firstPhoto.decode().then(afterDecode, afterDecode);
        else afterDecode();
      };
      if (firstPhoto && !firstPhoto.complete) {
        self.on(firstPhoto, 'load', startBuffer, { once: true });
        self.on(firstPhoto, 'error', startBuffer, { once: true });
      } else { startBuffer(); }
      $$('[data-bfv2-thumb]', this.root).forEach(function (th) {
        self.on(th, 'click', function () {
          var id = th.getAttribute('data-bfv2-thumb');
          var cells = $$('[data-bfv2-cell]', track).filter(function (c) { return !c.hidden; });
          var i = cells.findIndex(function (c) { return c.getAttribute('data-bfv2-cell') === id; });
          if (i < 0) return;
          if (th.hasAttribute('data-bfv2-thumb-demo')) {
            var demo = document.getElementById(th.getAttribute('data-bfv2-thumb-demo'));
            if (demo) { scrollToEl(demo); return; }
          }
          self.warmGallery(i);
          track.scrollTo({ left: i * track.clientWidth, behavior: REDUCED ? 'auto' : 'smooth' });
        });
      });
      var t;
      this.on(track, 'scroll', function () {
        clearTimeout(t);
        t = setTimeout(function () { self.syncThumbs(); }, 70);
      }, { passive: true });
    }
  };

  Controller.prototype.closeMix = function () {
    var toggle = $('[data-bfv2-mix-toggle]', this.root);
    var panel = $('[data-bfv2-mix-panel]', this.root);
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  };

  Controller.prototype.warmGallery = function (index) {
    var track = $('[data-bfv2-gal-track]', this.root);
    if (!track || (!this.galleryBuffered && index == null)) return;
    var cells = $$('[data-bfv2-cell]', track).filter(function (cell) { return !cell.hidden; });
    var current = index == null ? Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) : index;
    cells.slice(Math.max(0, current - 1), current + 3).forEach(function (cell) {
      var img = $('img', cell);
      if (!img) return;
      var selected = cells.indexOf(cell) === current;
      img.fetchPriority = selected ? 'high' : 'low';
      img.loading = 'eager';
    });
  };

  Controller.prototype.syncThumbs = function () {
    var track = $('[data-bfv2-gal-track]', this.root);
    if (!track) return;
    var cells = $$('[data-bfv2-cell]', track).filter(function (c) { return !c.hidden; });
    var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    var cur = cells[i] ? cells[i].getAttribute('data-bfv2-cell') : null;
    this.warmGallery();
    $$('[data-bfv2-thumb]', this.root).forEach(function (th) {
      th.setAttribute('aria-current', String(th.getAttribute('data-bfv2-thumb') === cur));
    });
  };

  /* ----------------------------------------------------------------- cart */

  Controller.prototype.bindCart = function () {
    var self = this;
    var btn = $('[data-bfv2-atc]', this.root);
    var buyNow = $('[data-bfv2-buynow]', this.root);
    var form = $('[data-bfv2-form]', this.root);
    if (form) {
      form.setAttribute('data-bfv2-ready', '');
      this.on(form, 'submit', function (e) { e.preventDefault(); self.addToCart(false); });
    }

    /* The theme header already intercepts `submit` on /cart/add forms in the
       capture phase. Owning the click means no submit event is dispatched, so
       the item cannot be added twice. Without JS the form still posts. */
    if (btn) {
      this.on(btn, 'click', function (e) {
        e.preventDefault();
        self.addToCart(false);
      });
    }
    if (buyNow) {
      this.on(buyNow, 'click', function (e) {
        e.preventDefault();
        self.addToCart(true);
      });
    }
  };

  Controller.prototype.setBusy = function (on, viaBuyNow) {
    var btn = $('[data-bfv2-atc]', this.root);
    var buyNow = $('[data-bfv2-buynow]', this.root);
    this.busy = on;
    if (btn) {
      btn.disabled = on || btn.hasAttribute('data-blocked');
      btn.setAttribute('aria-busy', String(on && !viaBuyNow));
      var spin = $('[data-bfv2-atc-spinner]', btn);
      if (spin) spin.hidden = !(on && !viaBuyNow);
    }
    if (buyNow) {
      buyNow.disabled = on || buyNow.hasAttribute('data-blocked');
      buyNow.setAttribute('aria-busy', String(on && !!viaBuyNow));
      var s2 = $('[data-bfv2-buynow-spinner]', buyNow);
      if (s2) s2.hidden = !(on && !!viaBuyNow);
    }
  };

  Controller.prototype.showError = function (msg) {
    var box = $('[data-bfv2-error]', this.root);
    if (!box) return;
    box.textContent = msg || '';
    box.hidden = !msg;
  };

  Controller.prototype.addToCart = function (thenCheckout) {
    var self = this;
    if (this.busy) return;                       /* no duplicate submissions */
    var got = this.pricingFor(this.tier, this.dark);
    if (!got.buyable || !got.variant) return;

    this.showError('');
    this.setBusy(true, thenCheckout);

    fetch(this.data.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: got.variant.items || [self.cartLine(got.variant)] })
    })
      .then(function (res) {
        return res.json().then(function (body) { return { ok: res.ok, body: body }; });
      })
      .then(function (r) {
        if (!r.ok) {
          var m = (r.body && (r.body.description || r.body.message)) || '';
          var e = new Error(m); e.fromServer = !!m; throw e;
        }
        if (thenCheckout) {
          /* straight to checkout, skipping the cart drawer, with any items the
             shopper already had still in the order — folded onto the offer first */
          var go = function () { window.location.href = self.data.routes.checkout; };
          if (window.vshNormalizeCart) { window.vshNormalizeCart().then(go, go); } else { go(); }
          return;
        }
        self.setBusy(false);
        self.openExistingCart();
      })
      .catch(function (err) {
        self.setBusy(false);
        var reason = (err && err.fromServer && err.message) ? err.message : self.data.errorGeneric;
        self.showError(reason + ' ' + self.data.errorSuffix);
      });
  };

  Controller.prototype.openExistingCart = function () {
    var trigger = document.getElementById('vshCartBtn');
    if (trigger) { trigger.click(); return; }
    var native = document.querySelector('cart-drawer-component');
    if (native && typeof native.open === 'function') {
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      native.open();
      return;
    }
    window.location.href = this.data.routes.cart;
  };

  function fmtMoney(cents) { return '$' + (cents / 100).toFixed(2); }
  /* "Save $37" rather than "Save $36.98" when the section asks for whole dollars */
  function fmtSave(cents, round) { return round ? '$' + Math.round(cents / 100) : fmtMoney(cents); }

  /* Pack count and bundle composition shown in the drawer, cart and checkout. */
  Controller.prototype.detailsLabel = function (variant) {
    var d = this.data, t = d.tiers[this.tier];
    var packsWord = t.packs === 1 ? d.packWord : d.packsWord;
    var label = t.packs + ' ' + packsWord + ' · ' + t.patches + ' ' + d.patchesLabel;
    if (d.freeThirdPack && t.packs === 3) label += ' · ' + this.compositionLabel(this.dark, t.packs);
    return label;
  };

  Controller.prototype.cartLine = function (variant) {
    var line = { id: variant.id, quantity: 1 };
    line.properties = { 'Details': this.detailsLabel(variant) };
    return line;
  };

  /* --------------------------------------------------------------- sticky */

  Controller.prototype.bindSticky = function () {
    var self = this;
    var bar = $('[data-bfv2-sticky]', this.root);
    /* Watch the button itself, not the whole buying column: the bar should
       arrive as soon as Add to cart leaves the screen, not several hundred
       pixels later once the guarantee and FAQ link have scrolled past too. */
    var anchor = $('[data-bfv2-atc]', this.root) || $('[data-bfv2-buybox]', this.root);
    if (!bar || !anchor) return;

    document.body.classList.add('bfv2-has-sticky');
    this.stickyVisible = false;
    this.liftAbovePreviewBar();

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        /* Only once the button has scrolled up and off the top of the screen.
           While it is still below the fold the shopper has not reached it yet,
           so there is nothing to bring back. */
        self.stickyVisible = !en.isIntersecting && en.boundingClientRect.top < 0;
        self.updateSticky();
      });
    }, { threshold: 0 });
    io.observe(anchor);

    var onResize = function () { self.liftAbovePreviewBar(); };
    window.addEventListener('resize', onResize, { signal: this.ac.signal });
    this.ac.signal.addEventListener('abort', function () { io.disconnect(); });

    var mo = new MutationObserver(function () { self.updateSticky(); });
    mo.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'open'] });
    this.ac.signal.addEventListener('abort', function () {
      mo.disconnect();
      document.body.classList.remove('bfv2-has-sticky');
    });
  };

  /* Shopify's theme-preview banner is a popover, so it paints in the browser's
     top layer above every z-index. While previewing, sit above it. It does not
     exist on a published page, where the bar stays flush to the bottom. */
  Controller.prototype.liftAbovePreviewBar = function () {
    var bar = $('[data-bfv2-sticky]', this.root);
    if (!bar) return;
    var pbar = document.getElementById('PBarNextFrameWrapper');
    var h = pbar ? Math.round(pbar.getBoundingClientRect().height) : 0;
    bar.style.bottom = h ? h + 'px' : '';
  };

  Controller.prototype.modalOpen = function () {
    var drawer = document.getElementById('vshDrawer');
    var panel = document.getElementById('vshPanel');
    if (drawer && drawer.classList.contains('open')) return true;
    if (panel && panel.classList.contains('open')) return true;
    if (document.querySelector('dialog[open]')) return true;
    if (document.querySelector('cart-drawer-component [open], .cart-drawer[open]')) return true;
    return false;
  };

  Controller.prototype.updateSticky = function () {
    var bar = $('[data-bfv2-sticky]', this.root);
    if (!bar) return;
    var got = this.pricingFor(this.tier, this.dark);
    var show = this.stickyVisible && !this.modalOpen() && got.buyable;
    bar.classList.toggle('is-shown', show);
    bar.setAttribute('aria-hidden', String(!show));
  };

  /* --------------------------------------------------------------- render */

  Controller.prototype.renderTiers = function () {
    var self = this, d = this.data;
    var wrap = $('[data-bfv2-tiers]', this.root);
    if (!wrap) return;
    wrap.innerHTML = d.tiers.map(function (t, i) {
      var dark = i === self.tier
        ? self.dark
        : self.nearestAllowed(i, self.customised
            ? Math.min(Math.max(Math.round(self.dark / self.currentTier().packs * t.packs), 0), t.packs)
            : self.pureDark(self.concern, t.packs));
      if (d.pairOffer) dark = t.packs === 2 ? self.pairDark : (d.defaultConcern || 0);
      var got = self.pricingFor(i, dark);
      var p = got.p;
      var tierSaveCents = (p.wasCents && p.wasCents > p.total) ? (p.wasCents - p.total) : 0;
      var on = i === self.tier;
      var packsWord = t.packs === 1 ? d.packWord : d.packsWord;
      var compact = d.tierStyle === 'chips' || d.tierStyle === 'cards';
      /* the automatic 'Save $X' figure for a discounted pack (12 Sep) */
      var saving = (compact && p.save && tierSaveCents > 0) ? esc(d.saveWord) + ' ' + fmtSave(tierSaveCents, d.saveRound) : '';
      /* a title that already opens with the count ("1 Pack") needs only the patch count under
         it; an offer name ("Buy 2, Get 1 Free") gets the pack count as well */
      var titleSaysCount = new RegExp('^\\s*' + t.packs + '(?!\\d)').test(t.title || '');
      return '<button type="button" class="bfv2-tier" role="radio" data-bfv2-tier="' + i + '"' +
        ' aria-checked="' + on + '" data-available="' + got.buyable + '">' +
        /* Chips have one slot on the border: the badge, else the saving pill. Cards show both —
           the badge on the border and the saving as a pill under the price. */
        (t.badge ? '<span class="bfv2-tier__badge">' + esc(t.badge) + '</span>'
          : (d.tierStyle === 'chips' && saving ? '<span class="bfv2-tier__badge">' + saving + '</span>' : '')) +
        (got.buyable ? '' : '<span class="bfv2-tier__na">' + esc(d.unavailableTierLabel) + '</span>') +
        '<span class="bfv2-tier__radio" aria-hidden="true"></span>' +
        '<span class="bfv2-tier__boxes" aria-hidden="true">' + self.bundleVisual(dark, t.packs) + '</span>' +
        '<span class="bfv2-tier__info">' +
          '<span class="bfv2-tier__title">' + esc(t.title) + '</span>' +
          /* chips and cards drop the pack words when the title already says "1 Pack" */
          '<span class="bfv2-tier__qty">' + (compact && titleSaysCount
              ? t.patches + ' ' + esc(d.patchesLabel)
              : t.packs + ' ' + esc(packsWord) + ' · ' + t.patches + ' ' + esc(d.patchesLabel)) + '</span>' +
          '<span class="bfv2-tier__ship">' + esc(t.freeShipping ? d.shipFreeLabel : d.shipPaidLabel) + '</span>' +
        '</span>' +
        '<span class="bfv2-tier__prices">' +
          '<span class="bfv2-tier__total">' + esc(p.money) +
            (p.was ? '<s class="bfv2-tier__was">' + esc(p.was) + '</s>' : '') +
          '</span>' +
          (p.save ? '<span class="bfv2-tier__save">' + esc(d.saveWord) + ' ' + esc(p.save) + '</span>' : '') +
          (d.tierStyle === 'cards' && saving ? '<span class="bfv2-tier__pill">' + saving + '</span>' : '') +
        '</span>' +
      '</button>';
    }).join('');
  };

  /* The first two packs follow the page; only the free pack changes type. */
  Controller.prototype.freePackConcern = function (dark, packs) {
    return this.data.defaultConcern === 1 ? dark - (packs - 1) : dark;
  };

  Controller.prototype.renderProductChoices = function () {
    var packs = this.currentTier().packs;
    var singleChoice = $('[data-bfv2-single-choice]', this.root);
    if (singleChoice && this.data.pairOffer) singleChoice.hidden = packs !== 1;
    $$('[data-bfv2-product-choice]', this.root).forEach(function (link) {
      var href = link.getAttribute(packs === 3 ? 'data-bfv2-choice-three' : 'data-bfv2-choice-one');
      if (href) {
        link.setAttribute('href', href);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
      var stock = $('[data-bfv2-choice-stock]', link);
      if (stock) stock.hidden = !!href;
    });
  };

  Controller.prototype.renderMix = function () {
    var self = this, d = this.data;
    var wrap = $('[data-bfv2-mix]', this.root);
    if (!wrap) return;
    var t = this.currentTier();
    /* one pack is chosen with the concern selector; there is nothing to mix */
    if (t.packs < 2) {
      wrap.hidden = true;
      var panel0 = $('[data-bfv2-mix-panel]', wrap);
      if (panel0) { panel0.innerHTML = ''; panel0.hidden = true; }
      var cur0 = $('[data-bfv2-mix-current]', wrap);
      if (cur0) { cur0.textContent = ''; cur0.hidden = true; }
      this.closeMix();
      return;
    }
    wrap.hidden = false;
    var panel = $('[data-bfv2-mix-panel]', wrap);
    if (d.freeThirdPack || d.pairOffer) {
      if (panel) panel.hidden = false;
      var oldSummary = $('[data-bfv2-mix-current]', wrap);
      if (oldSummary) oldSummary.hidden = true;
    }
    if (panel) {
      panel.innerHTML = this.compositions(this.tier).map(function (c) {
        var on = c.dark === self.dark;
        var freeConcern = self.freePackConcern(c.dark, t.packs);
        return '<button type="button" class="bfv2-mix__opt" role="radio" data-bfv2-mixopt="' + c.dark + '"' +
          ' aria-checked="' + on + '" data-available="' + c.available + '"' + (c.available ? '' : ' disabled') + '>' +
          '<span class="bfv2-mix__radio" aria-hidden="true"></span>' +
          '<span class="bfv2-mix__vis" aria-hidden="true">' + self.bundleVisual(d.freeThirdPack ? freeConcern : c.dark, d.freeThirdPack ? 1 : t.packs) + '</span>' +
          '<span class="bfv2-mix__txt">' + esc(d.pairOffer ? (c.dark === 1 ? 'One of each' : (c.dark === 0 ? '2 Blemish' : '2 Dark Spot')) : (d.freeThirdPack ? d.concernShort[freeConcern] : self.compositionLabel(c.dark, t.packs))) +
            (c.available ? '' : ' <span class="bfv2-mix__na">' + esc(d.unavailableMixLabel) + '</span>') +
          '</span>' +
        '</button>';
      }).join('');
    }
    if (!d.freeThirdPack && !d.pairOffer) this.renderMixSummary();
    var note = $('[data-bfv2-mix-note]', wrap);
    if (note) {
      var mixed = this.dark > 0 && this.dark < t.packs;
      note.textContent = mixed ? d.mixNote : '';
      note.hidden = !mixed;
    }
  };

  /* compact summary of the chosen mix while the panel is collapsed */
  Controller.prototype.renderMixSummary = function () {
    var cur = $('[data-bfv2-mix-current]', this.root);
    var toggle = $('[data-bfv2-mix-toggle]', this.root);
    if (!cur || !toggle) return;
    var collapsed = toggle.getAttribute('aria-expanded') !== 'true';
    var t = this.currentTier();
    cur.textContent = this.compositionLabel(this.dark, t.packs);
    cur.hidden = !collapsed || t.packs < 2;
  };

  Controller.prototype.render = function () {
    var self = this, d = this.data;
    var t = this.currentTier();
    var got = this.pricingFor(this.tier, this.dark);
    var p = got.p;
    var mixed = this.dark > 0 && this.dark < t.packs;

    $$('[data-bfv2-concern]', this.root).forEach(function (btn) {
      var c = parseInt(btn.getAttribute('data-bfv2-concern'), 10);
      btn.setAttribute('aria-checked', String(c === self.concern));
    });

    this.renderTiers();
    this.renderMix();
    this.renderProductChoices();

    /* benefits: the shared lines always, plus the relevant formula(s) */
    var seen = { szinc: false, mela: false };
    $$('[data-bfv2-ben]', this.root).forEach(function (li) {
      var which = li.getAttribute('data-bfv2-ben');
      var show;
      if (which === 'both') show = true;
      else if (mixed && !d.freeThirdPack && !d.pairOffer) { show = !seen[which]; if (show) seen[which] = true; }
      else show = (which === (self.concern === 0 ? 'szinc' : 'mela'));
      li.hidden = !show;
    });

    var state = [mixed ? 'mixed' : 'pure', this.concern === 0 ? 'szinc' : 'mela', 'any'];
    $$('[data-bfv2-show-for]').forEach(function (el) {
      var terms = el.getAttribute('data-bfv2-show-for').trim().split(/\s+/);
      el.hidden = !terms.some(function (term) {
        return term.split('+').every(function (tok) { return state.indexOf(tok) > -1; });
      });
    });

    $$('[data-bfv2-cell]', this.root).forEach(function (cell) {
      var f = cell.getAttribute('data-bfv2-for') || 'any';
      cell.hidden = !(f === 'any' || f === (self.concern === 0 ? 'szinc' : 'mela'));
    });
    $$('[data-bfv2-thumb]', this.root).forEach(function (th) {
      var f = th.getAttribute('data-bfv2-for') || 'any';
      th.hidden = !(f === 'any' || f === (self.concern === 0 ? 'szinc' : 'mela'));
    });
    var track = $('[data-bfv2-gal-track]', this.root);
    if (track && this.ready) track.scrollTo({ left: 0 });
    this.syncThumbs();

    var packsWord = t.packs === 1 ? d.packWord : d.packsWord;
    var freeShip = !!t.freeShipping;
    var text = {
      entryPrice: (d.tiers[0] && (this.variantFor(0, this.pureDark(this.concern, d.tiers[0].packs)) || d.tiers[0].proposed).money) || '',
      total: p.money,
      atcTotal: d.atcJoiner + ' ' + p.money,
      packName: t.packs + ' ' + packsWord,
      patchTotal: t.patches + ' ' + d.patchesLabel,
      summary: t.packs + ' ' + packsWord + ' · ' + t.patches + ' ' + d.patchesLabel,
      stickySummary: t.packs + ' ' + packsWord + ' · ' + p.money,
      stickyMeta: mixed ? this.compositionLabel(this.dark, t.packs) : (freeShip ? d.shipFreeLabel : d.shipPaidLabel),
      mixLabel: (mixed || (d.pairOffer && t.packs === 2)) ? this.compositionLabel(this.dark, t.packs) : '',
      concernName: d.concernNames[this.concern],
      concernTech: d.concernTech[this.concern],
      save: p.save || '',
      was: p.was || '',
      saveLine: p.save ? d.saveWord + ' ' + p.save : ''
    };
    $$('[data-bfv2-text]').forEach(function (el) {
      var k = el.getAttribute('data-bfv2-text');
      if (!(k in text)) return;
      el.textContent = text[k];
      if (el.hasAttribute('data-bfv2-hide-empty')) el.hidden = !text[k];
    });

    $$('[data-bfv2-boxes]').forEach(function (el) {
      el.innerHTML = self.bundleVisual(self.dark, t.packs);
    });

    $$('[data-bfv2-ship]').forEach(function (el) {
      el.hidden = !((el.getAttribute('data-bfv2-ship') === 'free') === freeShip);
    });

    /* add to cart */
    var input = $('[data-bfv2-variant-input]', this.root);
    /* Cleared when the selection has no variant, so even a native form post
       cannot buy a different pack than the one on screen. */
    if (input) input.value = got.variant && got.variant.id ? got.variant.id : '';
    var quantity = $('[data-bfv2-quantity-input]', this.root);
    var itemInputs = $('[data-bfv2-item-inputs]', this.root);
    var pair = d.pairOffer && t.packs === 2;
    if (input) input.disabled = pair;
    if (quantity) quantity.disabled = pair;
    if (itemInputs) itemInputs.innerHTML = pair && got.buyable ? got.variant.items.map(function (item, index) {
      return '<input type="hidden" name="items[' + index + '][id]" value="' + item.id + '">' +
        '<input type="hidden" name="items[' + index + '][quantity]" value="' + item.quantity + '">';
    }).join('') : '';
    var atc = $('[data-bfv2-atc]', this.root);
    if (atc) {
      atc.toggleAttribute('data-blocked', !got.buyable);
      atc.disabled = !got.buyable || this.busy;
      var lbl = $('[data-bfv2-atc-label]', atc);
      if (lbl) lbl.textContent = got.buyable ? (d.pairOffer ? 'Add ' + t.packs + ' ' + packsWord : d.atcLabel) : d.unavailableTierLabel;
      var pr = $('[data-bfv2-atc-price]', atc);
      if (pr) {
        pr.textContent = got.buyable ? text.atcTotal : "";
        pr.hidden = !got.buyable;
      }
    }
    var buyNow = $('[data-bfv2-buynow]', this.root);
    if (buyNow) {
      buyNow.toggleAttribute('data-blocked', !got.buyable);
      buyNow.disabled = !got.buyable || this.busy;
    }
    /* a wallet button must never act on a selection that has no variant */
    var wallet = $('[data-bfv2-express-wallet]', this.root);
    if (wallet) wallet.hidden = !got.buyable || pair;

    /* editor-only setup notice for a tier that is not configured yet */
    var setup = $('[data-bfv2-setup]', this.root);
    if (setup) {
      var need = !got.buyable && d.designMode;
      setup.hidden = !need;
      if (need) {
        setup.textContent = 'Setup needed: no Shopify variant matches "' + t.packValue +
          ' / ' + this.patchValue(this.dark, t.packs) + '". Create it before this tier can be sold.';
      }
    }

    if (this.unmappedVariant) {
      /* the incoming link pointed at something outside this offer */
      this.showError(d.unmappedNotice || '');
      this.unmappedVariant = null;
    }

    this.syncUrl(got.variant);
    this.updateSticky();

    document.dispatchEvent(new CustomEvent('bfv2:change', {
      detail: { tier: this.tier, concern: this.concern, dark: this.dark, variant: got.variant, mixed: mixed }
    }));
  };

  /* The picture for one composition. Purpose-made artwork wins; otherwise the
     stack is built from the two single-box cutouts so every combination still
     shows the right number and colour of boxes. */
  Controller.prototype.bundleVisual = function (dark, packs) {
    if (this.data.pairOffer && packs === 2) return this.boxesHtml(this.boxesFor(dark, packs));
    var art = this.data.bundleImages && this.data.bundleImages[packs + '|' + dark];
    if (art && art.url) {
      return '<img class="bfv2-bundleart" src="' + esc(art.url) + '" ' +
             (art.srcset ? 'srcset="' + esc(art.srcset) + '" sizes="' + (packs === 1 ? '72px' : '160px') + '" ' : '') + 'alt="" ' +
             'width="200" height="200" loading="lazy" decoding="async">';
    }
    return this.boxesHtml(this.boxesFor(dark, packs));
  };

  Controller.prototype.boxesHtml = function (boxes) {
    var d = this.data;
    var cls = boxes.length > 3 ? ' bfv2-boxes--tight' : '';
    return '<span class="bfv2-boxes__row' + cls + '">' + boxes.map(function (c) {
      var src = c === 0 ? d.cutoutSzinc : d.cutoutMela;
      var alt = c === 0 ? d.concernShort[0] : d.concernShort[1];
      var inner = src
        ? '<img src="' + esc(src) + '" alt="" width="80" height="107" loading="lazy" decoding="async">'
        : '<span class="bfv2-boxes__ph bfv2-boxes__ph--' + (c === 0 ? 'szinc' : 'mela') + '"></span>';
      return '<span class="bfv2-boxes__b" title="' + esc(alt) + '">' + inner + '</span>';
    }).join('') + '</span>';
  };

  Controller.prototype.destroy = function () { this.ac.abort(); };

  /* ------------------------------------------------------- generic widgets */

  function initAccordions(scope, ac) {
    $$('[data-bfv2-acc-btn]', scope).forEach(function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      }, { signal: ac.signal });
    });
  }

  function initVideos(scope, ac) {
    $$('[data-bfv2-video]', scope).forEach(function (wrap) {
      var btn = $('[data-bfv2-video-play]', wrap);
      var src = wrap.getAttribute('data-bfv2-video');
      if (!btn || !src) return;
      btn.addEventListener('click', function () {
        var v = document.createElement('video');
        v.src = src;
        v.controls = true;
        v.playsInline = true;
        v.preload = 'metadata';
        var poster = wrap.getAttribute('data-bfv2-poster');
        if (poster) v.poster = poster;
        wrap.innerHTML = '';
        wrap.appendChild(v);
        v.play().catch(function () { /* the shopper can press play themselves */ });
      }, { signal: ac.signal });
    });
  }

  /* --------------------------------------------------------------- wiring */

  var registry = new Map();

  function initScope(scope) {
    $$('[data-bfv2-root]', scope).forEach(function (root) {
      if (registry.has(root)) return;              /* never bind twice */
      registry.set(root, new Controller(root));
    });
    var ac = new AbortController();
    initAccordions(scope, ac);
    initVideos(scope, ac);
    if (scope !== document) registry.set(scope, { destroy: function () { ac.abort(); } });
  }

  /* One delegated handler for every in-page jump, including links that live in
     other sections and links added when a section is reloaded in the editor. */
  function initJumps() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('[data-bfv2-jump]') : null;
      if (!a) return;
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      var target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      scrollToEl(target);
      var h = target.querySelector('h2, h3, button, [tabindex]');
      if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: true }); }
    });
  }

  /* Score and count come from the review records rendered on the page, so the
     header can never claim a rating the shopper cannot scroll down and verify. */
  function renderRating() {
    var host = document.querySelector('[data-bfv2-rating]');
    if (!host) return;
    var records = $$('[data-bfv2-review-rating]');
    var values = records
      .map(function (el) { return parseFloat(el.getAttribute('data-bfv2-review-rating')); })
      .filter(function (n) { return !isNaN(n) && n > 0; });
    if (!values.length) { host.hidden = true; return; }
    var avg = values.reduce(function (a, b) { return a + b; }, 0) / values.length;
    var shown = Math.round(avg * 10) / 10;
    var fg = host.querySelector('.bfv2-stars__fg');
    if (fg) fg.style.width = (avg / 5 * 100) + '%';
    var stars = host.querySelector('[data-bfv2-rating-stars]');
    if (stars) stars.setAttribute('aria-label', shown + ' out of 5, from ' + values.length + ' reviews');
    var text = host.querySelector('[data-bfv2-rating-text]');
    if (text) {
      text.textContent = shown.toFixed(1) + ' · ' + values.length +
        (values.length === 1 ? ' review' : ' reviews');
    }
    host.hidden = false;
  }

  function boot() { initScope(document); initJumps(); renderRating(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: sections are replaced wholesale, so tear the old one down
     before wiring the new markup. */
  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll && $$('[data-bfv2-root]', e.target).forEach(function (root) {
      var c = registry.get(root);
      if (c && c.destroy) c.destroy();
      registry.delete(root);
    });
    var self = registry.get(e.target);
    if (self && self.destroy) { self.destroy(); registry.delete(e.target); }
  });

  document.addEventListener('shopify:section:load', function (e) {
    initScope(e.target);
    renderRating();
    /* satellite sections need the current selection applied immediately */
    registry.forEach(function (c) { if (c && c.render && c.ready) c.render(); });
  });
})();
